import React from 'react';
import { IconLoyalty } from '@noths/polaris-client-ribbons-design-system';

import { DeliveryEventMessage } from 'src/components/atoms/DeliveryEventMessage/DeliveryEventMessage';
import type { DeliveryEvent, DeliveryOption } from 'src/redux/delivery/types';
import { deliveryOptionArrivesOnOrByDate } from './utils/deliveryOptionArrivesOnOrByDate';
import { getCutoffMessage } from './utils/getCutoffMessage';
import { getDeliveryOptionText } from './utils/getDeliveryOptionText';
import { getFormattedDeliveryDate } from './utils/getFormattedDeliveryDate';
import { getFormattedPrice } from './utils/getFormattedPrice';
import * as styles from './DeliveryOptions.styles';

interface DeliveryOptionsProps {
  deliveryEvent?: DeliveryEvent;
  deliveryOptions: DeliveryOption[] | [];
  partnerName: string;
  specialSize?: boolean;
}

export const DeliveryOptions = ({
  deliveryEvent,
  deliveryOptions,
  partnerName,
  specialSize = false,
}: DeliveryOptionsProps) => {
  const hasOptionThatArrivesByEvent = deliveryEvent
    ? deliveryOptions.some((option) => deliveryOptionArrivesOnOrByDate(option, deliveryEvent.date))
    : false;
  const hasFreeOption = deliveryOptions.some((option) => Number(option.price.amount) === 0);

  return (
    <>
      {deliveryEvent && hasOptionThatArrivesByEvent && (
        <DeliveryEventMessage eventName={deliveryEvent.name} />
      )}
      <ul css={styles.deliveryOptionsList}>
        {deliveryOptions.map((deliveryOption) => {
          const {
            cutoffDateTime,
            date,
            exactDate,
            max,
            min,
            optionCode,
            partnerFreeDeliveryThresholdPrice,
            preSalePrice,
            price: { amount, currencyCode },
          } = deliveryOption;
          const optionPrice = Number(amount);
          const cutoffMessage = getCutoffMessage(cutoffDateTime);
          const formattedPrice = getFormattedPrice(optionPrice, currencyCode);
          const showArrivesBy = deliveryEvent
            ? deliveryOptionArrivesOnOrByDate(deliveryOption, deliveryEvent.date)
            : false;

          return (
            <li css={styles.item} key={optionCode}>
              <div css={styles.optionInfo}>
                <span>
                  <h6 css={styles.optionHeading}>
                    {exactDate && date ? (
                      <b>
                        {optionCode === 'SATURDAY' ? 'Get it on ' : 'Estimated delivery '}
                        <time dateTime={date}>{getFormattedDeliveryDate(date)}</time>
                      </b>
                    ) : (
                      `${min}-${max} days`
                    )}
                    {showArrivesBy ? (
                      <span>
                        {' '}
                        · Arrives in time for{' '}
                        <time dateTime={deliveryEvent!.date}>{deliveryEvent!.name}</time>
                      </span>
                    ) : null}
                  </h6>
                  <p css={styles.optionBody}>
                    {cutoffMessage ? `Order by ${cutoffMessage} · ` : ''}
                    {specialSize ? 'Special size delivery' : getDeliveryOptionText(optionCode)}
                  </p>
                </span>
                <span css={styles.optionPrices}>
                  {preSalePrice && (
                    <span>
                      {getFormattedPrice(Number(preSalePrice.amount), preSalePrice.currencyCode)}
                    </span>
                  )}
                  <b css={styles.optionPrice} data-price={optionPrice}>
                    {formattedPrice}
                  </b>
                </span>
              </div>
              {partnerFreeDeliveryThresholdPrice && !hasFreeOption && (
                <p css={styles.freePartnerDelivery}>
                  <span>
                    <IconLoyalty color={'var(--functional-green-base)'} height={16} width={16} />
                  </span>
                  <span>
                    <b>Free standard delivery</b> when you spend over{' '}
                    {getFormattedPrice(
                      Number(partnerFreeDeliveryThresholdPrice.amount),
                      partnerFreeDeliveryThresholdPrice.currencyCode,
                    )}{' '}
                    with {partnerName}
                  </span>
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
};
